/*---------------------------------------------------------------------------------------------
 *  Copyright (c) Microsoft Corporation. All rights reserved.
 *  Licensed under the MIT License. See License.txt in the project root for license information.
 *--------------------------------------------------------------------------------------------*/
/* tslint:disable: no-duplicate-imports */
import classnames from 'classnames';
import { action, observable, reaction, set } from 'mobx';
import { observer } from 'mobx-react';
import * as React from 'react';

import { getTelemetryObject, IModuleProps, ITelemetryContent } from '@msdyn365-commerce-modules/utilities';
import {
    Address,
    AddressPurpose,
    CountryRegionInfo,
    StateProvinceInfo
} from '@msdyn365-commerce/retail-proxy/dist/Entities/CommerceTypes.g';
import { getCheckoutState } from '@msdyn365-commerce/global-state';
import { IContext } from '@msdyn365-commerce/retail-proxy';
import { vSIInitiateRestCallAsync } from '../../dotdigital-extension/DataActionExtension.g';
import { GetCustomerAddressInput } from '../../themes/dobbies/actions/get-customer-address.action';
import { IDeliveryOption, IPostCodeDropDownOption, IPostCodeVerification } from '../dobbies-checkout-shipping-address';
import { AutoSuggest } from './common/address-autosuggest';
import { IAutoSuggestOptions } from './common/address-autosuggest.data';
import { AddressCommon } from './common/address-common';
import { AddressFormat } from './common/address-format';
import { AddressItemType } from './common/address-format.data';
import { AddressMetaData } from './common/address-meta-data';
import { AddressOperation, AddressType, IAddressResource, IAddressResponse } from './common/address-module.data';
import { AddressAddUpdate, IAddressAddUpdateProps } from './common/components/address-add';
import { AddressList, IAddressListProps } from './common/components/address-list';
import { IDobbiesLoyaltyAccountManagementAddressData } from './dobbies-loyalty-account-management-address.data';
import { IDobbiesLoyaltyAccountManagementAddressProps } from './dobbies-loyalty-account-management-address.props.autogenerated';
import { InputFieldValidation } from '../../themes/dobbiesclub/actions/input-field-validation-action';

export interface IAccountManagementAddressViewState {
    isShowAddresList: boolean;
    isShowAddOrUpdateAddress: boolean;
}
export interface IDobbiesLoyaltyAccountManagementAddressState {
    postCodeEmptyErrorMessage: string;
    addressOptions: IPostCodeDropDownOption[];
}

export interface IAccountManagementAddressViewProps
    extends React.Component<IDobbiesLoyaltyAccountManagementAddressProps<IDobbiesLoyaltyAccountManagementAddressData>> {
    className: string;
    currentOperation: AddressOperation;
    selectedAddress?: Address;
    addUpdateAddress: Address;
    countryRegionId: string;
    stateProvinceInfo?: StateProvinceInfo[];
    customerAddresses: Address[];
    validationError: object;
    addressActionResponse?: IAddressResponse;
    viewState: IAccountManagementAddressViewState;
    AccountAddressManagement: IModuleProps;
    isUpdating: boolean;
    showAddressList: IAddressListProps;
    showAddOrUpdateAddress: IAddressAddUpdateProps;
    renderPostCodeSearchInput: React.ReactNode;
}

/**
 *
 * Address component
 * @extends {React.Component<IDobbiesLoyaltyAccountManagementAddressProps<IDobbiesLoyaltyAccountManagementAddressData>>}
 */
@observer
class AccountManagementAddress extends React.Component<
    IDobbiesLoyaltyAccountManagementAddressProps<IDobbiesLoyaltyAccountManagementAddressData>,
    IDobbiesLoyaltyAccountManagementAddressState
> {
    @observable private currentOperation: AddressOperation;
    @observable private selectedAddress?: Address;
    @observable private countryRegionId: string = 'USA';
    @observable private stateProvinceInfo?: StateProvinceInfo[];
    @observable private customerAddresses: Address[];
    @observable private addUpdateAddress: Address;
    @observable private validationError: object;
    @observable private addressActionResponse?: IAddressResponse;
    @observable private isUpdating?: boolean;
    @observable private hasError?: boolean;

    private addressCommon: AddressCommon;
    private addressFormat: AddressFormat;
    private countryRegions: CountryRegionInfo[];
    private addressPurposes: AddressPurpose[];
    private resources: IAddressResource;
    private defaultAddressType: number = 6; // Default to Home
    private telemetryContent: ITelemetryContent;
    private autoSuggest?: AutoSuggest;
    /* VSI-Customization -- START post code input ref  */
    private postCodeInputRef: React.RefObject<HTMLInputElement>;
    /* VSI-Customization -- END  */

    constructor(props: IDobbiesLoyaltyAccountManagementAddressProps<IDobbiesLoyaltyAccountManagementAddressData>) {
        super(props);
        const { context, data, resources, telemetry } = this.props;

        this.resources = resources;
        if (!this.props.config.isNewLoyaltySignupJourney) {
            this.currentOperation = AddressOperation.List;
        } else {
            this.currentOperation = AddressOperation.Update;
        }
        this.countryRegions = data.countryRegions.result || [];
        this.addressPurposes = data.addressPurposes.result || [];
        this.customerAddresses = data.address.result || [];
        this.addUpdateAddress = {};
        this.validationError = {};
        this.addressCommon = new AddressCommon(context, resources, telemetry);
        this.addressFormat = new AddressFormat(
            this.countryRegions,
            new AddressMetaData({ ...resources }, this._getAddressFormatExcludeList()),
            this.addressPurposes
        );
        this.telemetryContent = getTelemetryObject(
            this.props.context.request.telemetryPageName!,
            this.props.friendlyName,
            this.props.telemetry
        );

        /* VSI-Customization -- START post code lookup related (input ref and state)  */
        this.postCodeInputRef = React.createRef<HTMLInputElement>();
        this.state = {
            addressOptions: [],
            postCodeEmptyErrorMessage: ''
        };
        /* VSI-Customization -- END  */
    }

    public async componentDidMount(): Promise<void> {
        const {
            context: {
                telemetry,
                actionContext: {
                    requestContext: { channel }
                }
            },
            config: { autoSuggestionEnabled, autoSuggestOptions }
        } = this.props;
        // VSI- Customization -- Update the current operation based on user address(s)
        const addressCount = (await this.props.data.address).length;
        if (addressCount === 0) {
            this._goToAddAddress();
        }
        // Update custom data action and share state across module
        this.props.context.actionContext.update(new GetCustomerAddressInput(), { addresses: this.props.data.address.result });

        if (this.props.config.isNewLoyaltySignupJourney && this.props.data.address.result && this.props.data.address.result.length) {
            const address = this.props.data.address.result[this.props.data.address.result.length - 1];
            set(this.addUpdateAddress, { ['Street']: '' });
            set(this.addUpdateAddress, { ['ZipCode']: address.ZipCode });
            set(this.addUpdateAddress, { ['CountyName']: address.CountyName });
            set(this.addUpdateAddress, { ['City']: address.City });
            set(this.addUpdateAddress, { ['State']: address.State });
            set(this.addUpdateAddress, { ['DistrictName']: address.DistrictName });
            set(this.addUpdateAddress, { ['FullAddress']: address.FullAddress });
            set(this.addUpdateAddress, { ['Name']: address.Name });
            set(this.addUpdateAddress, { ['Phone']: address.Phone });
            set(this.addUpdateAddress, { ['ThreeLetterISORegionName']: address.ThreeLetterISORegionName });
            set(this.addUpdateAddress, { ['TwoLetterISORegionName']: address.TwoLetterISORegionName });
            // Bing autosuggest put the complete address in the Street input box. Updating the street input box to show only street address.
            setTimeout(() => {
                set(this.addUpdateAddress, { ['Street']: address.Street });
            }, 0);
            getCheckoutState(this.props.context.actionContext)
                .then(checkoutState => {
                    checkoutState
                        .updateShippingAddress({
                            newShippingAddress: address
                        })
                        .then(rsp => {
                            console.log('Shipping Address updated', rsp);
                        })
                        .catch(e => {
                            console.log('Error updating addr', e);
                        });
                })
                .catch(e => {});
        }

        this.props.data.countryRegions.then(() => {
            this._setDefaultCountryRegionId();
        });

        reaction(
            () => this.countryRegionId,
            () => this._getStateProvinces()
        );

        reaction(
            () => this.currentOperation,
            () => {
                this._getStateProvinces();
            }
        );

        if (autoSuggestionEnabled) {
            if (channel && !channel.BingMapsApiKey) {
                telemetry.error('BingMapsApiKey is missing.');
                return;
            }

            if (channel && !channel.BingMapsEnabled) {
                telemetry.error('Map is disabled from HQ.');
                return;
            }

            const options: IAutoSuggestOptions = { ...autoSuggestOptions };
            this.autoSuggest = new AutoSuggest(
                telemetry,
                options,
                channel?.BingMapsApiKey,
                channel?.ChannelCountryRegionISOCode,
                channel?.DefaultLanguageId
            );

            reaction(
                () =>
                    this.props.data.storeSelectorStateManager.result?.loadMapApi !== undefined &&
                    (this.currentOperation === AddressOperation.Add || this.currentOperation === AddressOperation.Update),
                async () => {
                    await this.autoSuggest?._loadMapAPI(await this.props.data.storeSelectorStateManager);
                }
            );

            reaction(
                () => {
                    this.props.data.storeSelectorStateManager.result?.isMapApiLoaded;
                },
                async () => {
                    if (this.currentOperation === AddressOperation.Add || this.currentOperation === AddressOperation.Update) {
                        await this._attachMapAutoSuggest();
                    }
                }
            );
        }
        if (this.props.config.isNewLoyaltySignupJourney) {
            this.addFocusOutListeners();
        }
    }

    public async componentDidUpdate(): Promise<void> {
        if (this.currentOperation === AddressOperation.Add || this.currentOperation === AddressOperation.Update) {
            if (this.props.data.storeSelectorStateManager.result?.isMapApiLoaded) {
                await this._attachMapAutoSuggest();
            }
        } else {
            this.autoSuggest?.disposeAutoSuggest();
        }
    }

    public render(): JSX.Element {
        const { renderView, config } = this.props;

        const viewProps = {
            ...this.props,
            renderPostCodeSearchInput: this._renderPostCodeInputField(),
            currentOperation: this.currentOperation,
            selectedAddress: this.selectedAddress,
            addUpdateAddress: this.addUpdateAddress,
            countryRegionId: this.countryRegionId,
            stateProvinceInfo: this.stateProvinceInfo,
            customerAddresses: this.customerAddresses,
            validationError: this.validationError,
            addressActionResponse: this.addressActionResponse,
            isUpdating: this.isUpdating,
            hasError: this.hasError,
            className: config.className,
            viewState: {
                isShowAddresList: this.currentOperation === AddressOperation.List,
                isShowAddOrUpdateAddress: this.currentOperation !== AddressOperation.List
            },
            AccountAddressManagement: {
                moduleProps: this.props,
                className: classnames('ms-account-management-address', config.className)
            },
            showAddressList: this._renderListAddress(),
            showAddOrUpdateAddress: this._renderAddOrUpdateAddress()
        };

        return renderView(viewProps) as React.ReactElement;
    }

    /* VSI-Customization -- START Post Code Lookup related code  */
    private findAddress = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const { value } = e.currentTarget;
        if (value && value.length >= 5) {
            const context: IContext = { callerContext: this.props.context.actionContext };
            const urlParams = {
                key: '1024c-01f0c-70dd6-06464',
                postcode: this.postCodeInputRef.current?.value,
                response: 'data_formatted'
            };
            const res = await vSIInitiateRestCallAsync(
                context,
                'Fetchify',
                'get',
                "{'cache-control':'no-cache' , 'Accept':'application/json'}",
                '',
                JSON.stringify(urlParams),
                `/json/rapidaddress`
            );
            if (res.result) {
                const responseResult: IPostCodeVerification = JSON.parse(res.result);
                /* VSI-Customization -- START If response got from API map it into array and update it with state (setState)  */
                if (!responseResult.error_msg) {
                    const addressOptions = this.mapPostCodeOptions(responseResult);
                    this.setState({ addressOptions: addressOptions, postCodeEmptyErrorMessage: '' });
                    /* VSI-Customization -- START If error code returns it means postcode isn't valid  */
                } else {
                    this.setState({ addressOptions: [], postCodeEmptyErrorMessage: this.props.resources.validPostCodeErrorText });
                }
            }
        } else if (value.length < 5) {
            this.setState({ addressOptions: [], postCodeEmptyErrorMessage: '' });
        }
    };
    /* VSI-Customization -- END  */
    /* VSI-Customization -- START If postcode is invalid display the error text message that please enter a valid postcode  */
    private _renderEmptyPostCodeErrorMessage = () => {
        return (
            <>
                {this.state.postCodeEmptyErrorMessage && (
                    <div className='post-code'>
                        <p className='post-code-error'>{this.state.postCodeEmptyErrorMessage}</p>
                    </div>
                )}
            </>
        );
    };
    /* VSI-Customization -- END  */
    /* VSI-Customization -- START When user selects option from autosuggestion box embed the selected option values into form fields  */
    private handleAddressOption = (option: IPostCodeDropDownOption) => (e: React.MouseEvent<HTMLDivElement>) => {
        const { addressOptions } = this.state;
        if (addressOptions && addressOptions.length > 0) {
            const addressOption = addressOptions.find(x => x.option.toLowerCase() === option.option.toLowerCase());
            if (addressOption) {
                // populate values into form
                this.onAddressAddUpdate('City', addressOption.town);
                this.onAddressAddUpdate('Street', `${addressOption.postCode.line_1}, ${addressOption.postCode.line_2}`);
                this.onAddressAddUpdate('ZipCode', addressOption.code);
                // clear the postcode input field
                // clear the array
                this.postCodeInputRef.current!.value = '';
                this.setState({ addressOptions: [] });
                if (this.props.config.isNewLoyaltySignupJourney) {
                    // Address auto fill
                    this.props.context.actionContext.update(new InputFieldValidation(), { areAllFieldsValid: true });
                    this._onAddressAddUpdateSubmit();
                }
            } else {
                this.onAddressAddUpdate('City', '');
                this.onAddressAddUpdate('Street', '');
                this.onAddressAddUpdate('ZipCode', '');
            }
        }
    };
    /* VSI-Customization -- END  */
    /* VSI-Customization -- START Created JSX based on options retrieved from API  */
    private _renderAddressOptions = (): React.ReactNode => {
        const { addressOptions } = this.state;
        if (addressOptions && addressOptions.length > 0) {
            return (
                <div>
                    {addressOptions
                        .sort((a, b) => a.option.localeCompare(b.option))
                        .map((option: IPostCodeDropDownOption) => {
                            return (
                                // tslint:disable-next-line: react-a11y-event-has-role
                                <div key={option.dps} onClick={this.handleAddressOption(option)}>
                                    {option.option}
                                </div>
                            );
                        })}
                </div>
            );
        }
        return;
    };
    /* VSI-Customization -- END  */
    /* VSI-Customization -- START Render input field with button  */
    private _renderPostCodeInputField = () => {
        return (
            <div className='postcode-validation'>
                <input
                    type='text'
                    className='msc-address-form__input'
                    onChange={this.findAddress}
                    ref={this.postCodeInputRef}
                    name='postcode'
                    id='fetchify-postcode'
                    placeholder={this.props.resources.enterPostCodePlaceholderText}
                />
                {this.state.addressOptions?.length > 0 ? (
                    <div className='auto-suggestion'>{this._renderAddressOptions()}</div>
                ) : (
                    <div className='post-code-errorbox'>{this._renderEmptyPostCodeErrorMessage()}</div>
                )}
            </div>
        );
    };
    /* VSI-Customization -- END  */
    /* VSI-Customization -- START Map response data into array and update state with mapped array  */
    private mapPostCodeOptions = (data: IPostCodeVerification) => {
        const addressOptions: IPostCodeDropDownOption[] = [];
        data &&
            data.delivery_points.map((option: IDeliveryOption) => {
                // create object and push it into array of type IPostCodeDropDownOption
                const obj: IPostCodeDropDownOption = {
                    option: `${option.line_1}, ${option.line_2}, ${data.town}`,
                    dps: option.dps,
                    postCode: option,
                    town: data.town,
                    code: data.postcode
                };
                addressOptions.push(obj);
            });
        return addressOptions;
    };
    /* VSI-Customization -- END  */

    private _renderAddOrUpdateAddress = (): IAddressAddUpdateProps => {
        return AddressAddUpdate({
            isUpdating: this.isUpdating,
            resources: this.resources,
            addressType: AddressType.Shipping,
            addressFormat: this.addressFormat.getAddressFormat(this.addUpdateAddress.ThreeLetterISORegionName || this.countryRegionId),
            defaultCountryRegionId: this.countryRegionId,
            defaultAddressType: this.defaultAddressType,
            selectedAddress: this.addUpdateAddress,
            validationError: this.validationError,
            addressActionResponse: this.addressActionResponse,
            dropdownDisplayData: this.addressFormat.getPrefilledAddressDropdownData(
                this.resources.addressStateDefaultSelectionText,
                this.stateProvinceInfo
            ),
            telemetryContent: this.telemetryContent,
            customerAddress: this.props.config.isNewLoyaltySignupJourney ? this.props.data.address.result : undefined,
            onInputChange: this._onAddressAddUpdateInputChange,
            onDropdownChange: this._onAddressAddUpdateDropdownChange,
            onSave: this._onAddressAddUpdateSubmit,
            onCancel: this._resetView,
            /* VSI-Customization -- START Heading issue resolved  */
            addAddressHeading: this.props.config.addAddressHeading,
            editAddressHeading: this.props.config.editAddressHeading
            /* VSI-Customization -- END  */
        });
    };

    private _renderListAddress = (): IAddressListProps => {
        const { config } = this.props;

        return AddressList({
            isUpdating: this.isUpdating,
            selectedAddress: this.selectedAddress,
            addressFormat: this.addressFormat,
            addresses: this.customerAddresses,
            addressPurposes: this.addressPurposes,
            resources: this.resources,
            heading: config.heading,
            primaryAddressSectionHeading: config.primaryAddressSectionHeading,
            otherAddressSectionHeading: config.otherAddressSectionHeading,
            contextRequest: this.props.context.request,
            telemetryContent: this.telemetryContent,
            onEditAddress: this._goToEditAddress,
            onRemoveAddress: this._onRemoveAddress,
            onAddAddress: this._goToAddAddress,
            onUpdatePrimaryAddress: this._onSubmitUpdatePrimaryAddress
        });
    };

    private _attachMapAutoSuggest = async (): Promise<void> => {
        const {
            data: {
                storeSelectorStateManager: { result: storeSelectorStateManager }
            }
        } = this.props;

        if (storeSelectorStateManager?.isMapApiLoaded) {
            this.autoSuggest?.attachAutoSuggest('#shipping_addressstreet', '#shipping_addressstreet_container', this._onSuggestionSelected);
        }
    };

    @action
    private _onSuggestionSelected = async (result: Microsoft.Maps.ISuggestionResult): Promise<void> => {
        this._clearAddressFields();
        const address = this.addressFormat.getTranformedAddress(result, this.stateProvinceInfo);
        set(this.addUpdateAddress, { ['Street']: '' });
        set(this.addUpdateAddress, { ['ZipCode']: address.ZipCode });
        set(this.addUpdateAddress, { ['CountyName']: address.CountyName });
        set(this.addUpdateAddress, { ['City']: address.City });
        set(this.addUpdateAddress, { ['State']: address.State });
        set(this.addUpdateAddress, { ['DistrictName']: address.DistrictName });
        set(this.addUpdateAddress, { ['FullAddress']: address.FullAddress });
        // Bing autosuggest put the complete address in the Street input box. Updating the street input box to show only street address.
        setTimeout(() => {
            set(this.addUpdateAddress, { ['Street']: address.Street });
        }, 0);
    };

    @action
    private _clearAddressFields = (): void => {
        const addressFormatItem = this.addressFormat.getAddressFormat(
            this.addUpdateAddress.ThreeLetterISORegionName || this.countryRegionId
        );
        addressFormatItem.forEach(format => {
            if (
                this.addUpdateAddress.Name !== undefined &&
                this.addUpdateAddress.Name === format.name &&
                !this.autoSuggest?.excludedAddressFields.includes(format.name)
            ) {
                this.addUpdateAddress.Name = '';
            }
        });
        this._clearValidation();
    };

    @action
    private _clearValidation = (): void => {
        this.validationError = {};
    };

    private onAddressAddUpdate = (name: string, value: string | boolean | number) => {
        set(this.addUpdateAddress, { [name]: value });
        this.addressFormat.validateAddressFormat(this.addUpdateAddress, this.validationError, this.countryRegionId, name);
    };

    private addFocusOutListeners = () => {
        const allAddressInputs = document.getElementsByClassName('msc-address-form__input-text');
        if (allAddressInputs && allAddressInputs.length) {
            for (let i = 0; i < allAddressInputs.length; i++) {
                if (allAddressInputs[i] instanceof HTMLInputElement) {
                    const inputElem = allAddressInputs[i] as HTMLInputElement;
                    inputElem.addEventListener('focusout', (event: FocusEvent) => {
                        this.saveAddressOnAllInformationFilled();
                    });
                }
            }
        }
    };

    private saveAddressOnAllInformationFilled = () => {
        const allAddressInputs = document.getElementsByClassName('msc-address-form__input-text');
        const addressValues: string[] = [];
        if (allAddressInputs && allAddressInputs.length) {
            for (let i = 0; i < allAddressInputs.length; i++) {
                if (allAddressInputs[i] instanceof HTMLInputElement) {
                    const inputElem = allAddressInputs[i] as HTMLInputElement;
                    if (!inputElem.value) {
                        this.props.context.actionContext.update(new InputFieldValidation(), { areAllFieldsValid: false });
                        return;
                    }
                    addressValues.push(inputElem.value);
                }
            }
        }
        if (addressValues.length === allAddressInputs.length) {
            this.props.context.actionContext.update(new InputFieldValidation(), { areAllFieldsValid: true });
            this._onAddressAddUpdateSubmit();
        }
    };

    private _onAddressAddUpdateInputChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
        if (event.target.type === 'checkbox') {
            this.onAddressAddUpdate(event.target.name, event.target.checked);
        } else {
            const value = (event.target.value || '').replace(new RegExp('[<>]', 'gi'), '');
            this.onAddressAddUpdate(event.target.name, value);
        }
    };

    private _onAddressAddUpdateDropdownChange = (event: React.ChangeEvent<HTMLSelectElement>): void => {
        if (event.target.name === AddressItemType[AddressItemType.AddressTypeValue]) {
            this.onAddressAddUpdate(event.target.name, +event.target.value);
        } else {
            this.onAddressAddUpdate(event.target.name, event.target.value);
        }
        if (event.target.name === AddressItemType[AddressItemType.ThreeLetterISORegionName]) {
            this._onCountryChange(event.target.value);
        }
    };

    private _onAddressUpdate = (response: Promise<IAddressResponse>, onSuccess: (response: IAddressResponse) => void): void => {
        this.isUpdating = true;
        response.then(
            (result: IAddressResponse) => {
                if (result.address) {
                    // Update the custom data action and share state across modules
                    this.props.context.actionContext.update(new GetCustomerAddressInput(), { addresses: result.customerAddresses });
                    this.hasError = false;
                    onSuccess(result);
                } else {
                    this.hasError = true;
                    this.addressActionResponse = result;
                }
                this.isUpdating = false;
            },
            () => {
                this.hasError = true;
                this.isUpdating = false;
            }
        );
    };

    private setAddressAsCookie = () => {
        this.props.context.request.cookies.set('customer_loyalty_address', JSON.stringify(this.addUpdateAddress));
    };

    private _onAddressAddUpdateSubmit = (): void => {
        const twoLetterISORegionName = this.addressFormat.getTwoLetterISORegionName(this.countryRegionId);
        set(this.addUpdateAddress, { ['ThreeLetterISORegionName']: this.countryRegionId });
        set(this.addUpdateAddress, { ['TwoLetterISORegionName']: twoLetterISORegionName });
        const phoneNumCookie = this.props.context.actionContext.requestContext.cookies.get('loyalty_singup_phone');
        if (phoneNumCookie) {
            set(this.addUpdateAddress, { ['Phone']: phoneNumCookie.value });
        }
        let name = '';
        if (this.props.data.customerInformation.result?.FirstName) {
            name = this.props.data.customerInformation.result?.FirstName;
        }
        if (this.props.data.customerInformation.result?.LastName) {
            name = `${name} ${this.props.data.customerInformation.result?.LastName}`;
        }
        if (name) {
            set(this.addUpdateAddress, { ['Name']: name });
        }
        if (!this.addressFormat.validateAddressFormat(this.addUpdateAddress, this.validationError, this.countryRegionId)) {
            return;
        }

        if (this.props.config.isNewLoyaltySignupJourney) {
            this.setAddressAsCookie();
            return;
        }

        let response: Promise<IAddressResponse>;
        if (this.addressCommon.isAuthenticatedFlow()) {
            response =
                this.currentOperation === AddressOperation.Update
                    ? this.addressCommon.updateCustomerAddress(this.addUpdateAddress)
                    : this.addressCommon.addCustomerAddress(this.addUpdateAddress);
        } else {
            response = Promise.resolve({ address: this.addUpdateAddress });
        }

        this._onAddressUpdate(response, this._onAddOrUpdateSuccess);
    };

    private _onCountryChange = (countryRegionId: string) => {
        this.countryRegionId = countryRegionId;
        const twoLetterISORegionName = this.addressFormat.getTwoLetterISORegionName(countryRegionId);
        set(this.addUpdateAddress, { ['ThreeLetterISORegionName']: countryRegionId });
        set(this.addUpdateAddress, { ['TwoLetterISORegionName']: twoLetterISORegionName });
        this.autoSuggest?.changeAutoSuggestionCountryCode(twoLetterISORegionName);
        this._clearAddressFields();
    };

    private _getAddressFormatExcludeList = (): AddressItemType[] => {
        const { config } = this.props;
        const addressFormatExcludeList: AddressItemType[] = [];

        if (!config.showAddressType) {
            addressFormatExcludeList.push(AddressItemType.AddressTypeValue);
        }

        addressFormatExcludeList.push(AddressItemType.IsPrimary);

        return addressFormatExcludeList;
    };

    private _getStateProvinces = (): void => {
        if (
            !this.countryRegionId ||
            !(this.currentOperation === AddressOperation.Add || this.currentOperation === AddressOperation.Update)
        ) {
            return;
        }

        // tslint:disable-next-line:no-floating-promises
        this.addressCommon.getStateProvinces(this.countryRegionId).then((result: StateProvinceInfo[]) => {
            const stateInfo = result.some(state => state.StateId === this.addUpdateAddress.State);
            // Reset state if selected state not found in the list.
            if (!stateInfo) {
                set(this.addUpdateAddress, { ['State']: '' });
            }
            this.stateProvinceInfo = result;
        });
    };

    private _setDefaultCountryRegionId(): void {
        const { request } = this.props.context;
        const market = request && request.channel && request.channel.ChannelCountryRegionISOCode;

        this.countryRegionId = this.addressCommon.getDefaultCountryRegionId(this.countryRegionId, this.countryRegions, market);
    }

    private _updateCurrentOperation = (operation: AddressOperation) => {
        this.currentOperation = operation;
    };

    private _onSubmitUpdatePrimaryAddress = (address: Address): void => {
        this.selectedAddress = address;
        const response = this.addressCommon.updateCustomerPrimaryAddress(address);
        this._onAddressUpdate(response, this._onUpdatePrimaryAddressSuccess);
    };

    private _goToEditAddress = (address: Address): void => {
        this.addUpdateAddress = { ...address };
        this.countryRegionId = this.addUpdateAddress.ThreeLetterISORegionName || 'USA';
        this._updateCurrentOperation(AddressOperation.Update);
    };

    private _onRemoveAddress = (address: Address): void => {
        address.Deactivate = true;
        const response = this.addressCommon.updateCustomerAddress(address);
        this._onAddressUpdate(response, this._onAddOrUpdateSuccess);
    };

    private _goToAddAddress = (): void => {
        this.addUpdateAddress = {};
        this._setDefaultCountryRegionId();
        set(this.addUpdateAddress, { ['ThreeLetterISORegionName']: this.countryRegionId });
        set(this.addUpdateAddress, { ['AddressTypeValue']: this.defaultAddressType });
        this._setDefaultCountryRegionId();
        this._updateCurrentOperation(AddressOperation.Add);
    };

    private _onAddOrUpdateSuccess = (response: IAddressResponse): void => {
        if (response.customerAddresses) {
            this.customerAddresses = response.customerAddresses;
        }
        this._resetView();
        if (response.customerAddresses && response.customerAddresses.length) {
            const addr = response.customerAddresses[response.customerAddresses?.length - 1];
            getCheckoutState(this.props.context.actionContext)
                .then(checkoutState => {
                    checkoutState
                        .updateShippingAddress({
                            newShippingAddress: addr
                        })
                        .then(rsp => {
                            console.log('Shipping Address updated', rsp);
                        })
                        .catch(e => {
                            console.log('Error updating addr', e);
                        });
                })
                .catch(e => {});
        }
    };

    private _onUpdatePrimaryAddressSuccess = (response: IAddressResponse): void => {
        if (response.customerAddresses) {
            this.customerAddresses = response.customerAddresses;
        }
    };

    private _resetView = (): void => {
        this.selectedAddress = undefined;
        if (!this.props.config.isNewLoyaltySignupJourney) {
            this._updateCurrentOperation(AddressOperation.List);
        }
    };
}

export default AccountManagementAddress;
